import {
  CODE_GRADE_CLAMP,
  DEFAULT_RIDER_CONFIG,
  MAX_ACCEL_MPS2,
  MAX_SPEED_MPS,
  type RiderConfig,
  type RiderInput,
  type RiderState,
  type WorldRef,
} from "./types";

/** Air density at sea level, kg/m^3. */
const RHO = 1.225;
/** Gravitational acceleration, m/s^2. */
const G = 9.81;
/** Rotating-mass allowance for acceleration, kg. */
const M_EFF_EXTRA_KG = 2;
/**
 * Below this speed the power-to-force conversion is held at the force for
 * this speed. A true P/v force diverges at a standstill.
 */
const MIN_POWER_SPEED_MPS = 0.5;

const MAX_DRAFT = 0.4;
const DRAFT_BASE = 0.35;
const DRAFT_DECAY_M = 2.5;
const DRAFT_MIN_GAP_M = 0.5;
const DRAFT_MAX_GAP_M = 15;
const DRAFT_LANE_WIDTH_M = 2.5;

/** Longest fixed integration step. The game loop calls at 20 Hz (0.05 s). */
const MAX_SUBSTEP_S = 0.05;
/** Largest amount of time one call may advance, to bound work on a huge dt. */
const MAX_ADVANCE_S = 1;
/** Safety bound for zero-length edges or a broken next-edge chain. */
const MAX_EDGE_CROSSINGS = 64;
const SOLVE_TOLERANCE_MPS = 1e-9;
const SOLVE_ITERATIONS = 80;

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function finiteOr(value: number, fallback: number): number {
  return Number.isFinite(value) ? value : fallback;
}

/** Defensive normalization so malformed config or input cannot produce NaN. */
function normalizeConfig(config: RiderConfig): RiderConfig {
  return {
    massKg: clamp(finiteOr(config.massKg, DEFAULT_RIDER_CONFIG.massKg), 1, 1000),
    cda: clamp(finiteOr(config.cda, DEFAULT_RIDER_CONFIG.cda), 0, 10),
    crr: clamp(finiteOr(config.crr, DEFAULT_RIDER_CONFIG.crr), 0, 1),
    drivetrainEff: clamp(
      finiteOr(config.drivetrainEff, DEFAULT_RIDER_CONFIG.drivetrainEff),
      0,
      1,
    ),
  };
}

/** Grade as a fraction, clamped to the code's road limit. */
function normalizeGrade(gradePct: number): number {
  return clamp(finiteOr(gradePct, 0) / 100, -CODE_GRADE_CLAMP, CODE_GRADE_CLAMP);
}

function normalizeDraft(draft: number): number {
  return clamp(finiteOr(draft, 0), 0, MAX_DRAFT);
}

function edgeLength(edge: { length: number }): number {
  return Math.max(0, finiteOr(edge.length, 0));
}

/**
 * Resistive force at a speed, in newtons.
 * F(v) = m*g*sin(theta) + Crr*m*g*cos(theta) + 0.5*rho*CdA_eff*(v + wind)^2
 * where theta = atan(grade).
 */
function resistanceForce(
  speed: number,
  grade: number,
  config: RiderConfig,
  draft: number,
  wind: number,
): number {
  const theta = Math.atan(grade);
  const cda = config.cda * (1 - normalizeDraft(draft));
  const relative = speed + wind;
  return (
    config.massKg * G * Math.sin(theta) +
    config.crr * config.massKg * G * Math.cos(theta) +
    0.5 * RHO * cda * relative * relative
  );
}

/**
 * Steady-state speed for a given crank power. Returns meters per second.
 *
 * Solves F(v) * v = P * eta on v in [0, MAX_SPEED_MPS]. Bisection is used
 * instead of Newton because on a descent F(v) * v is not monotonic: it starts
 * at zero, can go negative, then rises to the terminal-speed root. A Newton
 * solve from a positive seed can jump to the negative branch and report
 * standstill for a coasting rider.
 *
 * With zero power a flat or uphill road returns 0. A descent returns its
 * terminal (coasting) speed, capped at MAX_SPEED_MPS.
 */
export function solveSpeed(
  powerWatts: number,
  gradePct: number,
  draft: number,
  config: RiderConfig,
  wind = 0,
): number {
  const cfg = normalizeConfig(config);
  const power = Math.max(0, finiteOr(powerWatts, 0)) * cfg.drivetrainEff;
  const grade = normalizeGrade(gradePct);
  const safeDraft = normalizeDraft(draft);
  const safeWind = finiteOr(wind, 0);

  const atMax = resistanceForce(MAX_SPEED_MPS, grade, cfg, safeDraft, safeWind) * MAX_SPEED_MPS;
  if (!Number.isFinite(atMax)) return 0;
  if (atMax <= power) return MAX_SPEED_MPS;

  let low = 0;
  let high = MAX_SPEED_MPS;
  for (let i = 0; i < SOLVE_ITERATIONS; i += 1) {
    const mid = (low + high) / 2;
    const output = resistanceForce(mid, grade, cfg, safeDraft, safeWind) * mid;
    if (output < power) {
      low = mid;
    } else {
      high = mid;
    }
    if (high - low < SOLVE_TOLERANCE_MPS) break;
  }

  const speed = (low + high) / 2;
  if (!Number.isFinite(speed) || speed < SOLVE_TOLERANCE_MPS) return 0;
  return clamp(speed, 0, MAX_SPEED_MPS);
}

interface Position {
  edgeId: number;
  s: number;
  ok: boolean;
}

/**
 * Move a position onto a valid edge. An s at or past the edge end advances
 * through as many edges as needed, up to the safety bound.
 */
function resolvePosition(world: WorldRef, edgeId: number, s: number): Position {
  let id = edgeId;
  let position = Math.max(0, s);

  for (let i = 0; i <= MAX_EDGE_CROSSINGS; i += 1) {
    const edge = world.getEdge(id);
    if (!edge) return { edgeId: id, s: position, ok: false };

    const length = edgeLength(edge);
    if (position < length) return { edgeId: id, s: position, ok: true };

    const next = world.nextEdge(id, length);
    if (next === null || !Number.isFinite(next)) {
      return { edgeId: id, s: length, ok: true };
    }

    id = next;
    position = Math.max(0, position - length);
  }

  return { edgeId: id, s: 0, ok: false };
}

interface Advance extends Position {
  moved: number;
}

/**
 * Move forward along the road graph, crossing edges and carrying the
 * remainder into the next edge. Returns ok=false at the end of the world or
 * when the graph cannot be followed, with the position parked at the wall.
 */
function advancePosition(
  world: WorldRef,
  edgeId: number,
  s: number,
  travel: number,
): Advance {
  let id = edgeId;
  let position = Math.max(0, s);
  let remaining = Math.max(0, finiteOr(travel, 0));
  let moved = 0;

  for (let i = 0; i <= MAX_EDGE_CROSSINGS; i += 1) {
    if (remaining <= 0) {
      return { edgeId: id, s: position, moved, ok: true };
    }

    const edge = world.getEdge(id);
    if (!edge) return { edgeId: id, s: position, moved, ok: false };

    const length = edgeLength(edge);
    if (position > length) position = length;
    const room = length - position;

    if (room > 0 && remaining < room) {
      position += remaining;
      moved += remaining;
      return { edgeId: id, s: position, moved, ok: true };
    }

    if (room > 0) {
      position = length;
      moved += room;
      remaining -= room;
    }

    const next = world.nextEdge(id, length);
    if (next === null || !Number.isFinite(next)) {
      return { edgeId: id, s: length, moved, ok: false };
    }

    if (remaining <= 0) {
      return { edgeId: next, s: 0, moved, ok: true };
    }

    id = next;
    position = 0;
  }

  return { edgeId: id, s: position, moved, ok: false };
}

/**
 * Advance one rider by dt seconds. The caller supplies the draft factor
 * (0..0.4) from the neighbors ahead. Pure function; does not mutate state.
 *
 * The game loop uses dt = 0.05 s. Larger values are split into fixed
 * substeps of at most 0.05 s; a single call advances at most 1 s so a bad
 * timestamp cannot teleport a rider across the world.
 */
export function stepRider(
  state: RiderState,
  input: RiderInput,
  dt: number,
  world: WorldRef,
  config: RiderConfig,
  draft = 0,
  wind = 0,
): RiderState {
  const cfg = normalizeConfig(config);
  const power = Math.max(0, finiteOr(input?.watts, 0));
  const safeDraft = normalizeDraft(draft);
  const safeWind = finiteOr(wind, 0);
  const riderId = finiteOr(state.riderId, 0);
  const lane = finiteOr(state.lane, 0);
  const safeEdgeId = finiteOr(state.edgeId, 0);
  const safeS = Math.max(0, finiteOr(state.s, 0));
  let speed = clamp(finiteOr(state.speed, 0), 0, MAX_SPEED_MPS);
  let distance = Math.max(0, finiteOr(state.distance, 0));

  const seconds = Number.isFinite(dt)
    ? Math.max(0, dt)
    : dt === Number.POSITIVE_INFINITY
      ? MAX_ADVANCE_S
      : 0;
  const position = resolvePosition(world, safeEdgeId, safeS);
  if (!position.ok) {
    return {
      ...state,
      riderId,
      edgeId: position.edgeId,
      s: Math.max(0, position.s),
      lane,
      speed: 0,
      power,
      distance,
    };
  }

  let edgeId = position.edgeId;
  let s = position.s;

  if (seconds <= 0) {
    return { ...state, riderId, edgeId, s, lane, speed, power, distance };
  }

  const total = Math.min(seconds, MAX_ADVANCE_S);
  const substeps = Math.max(1, Math.ceil(total / MAX_SUBSTEP_S));
  const step = total / substeps;

  for (let i = 0; i < substeps; i += 1) {
    const edge = world.getEdge(edgeId);
    if (!edge) {
      speed = 0;
      break;
    }

    const length = edgeLength(edge);
    const gradePct = finiteOr(world.sampleGrade(edgeId, clamp(s, 0, length)), 0);
    const grade = normalizeGrade(gradePct);
    const resist = resistanceForce(speed, grade, cfg, safeDraft, safeWind);
    const forceSpeed = Math.max(speed, MIN_POWER_SPEED_MPS);
    const rawAccel =
      (power * cfg.drivetrainEff / forceSpeed - resist) / (cfg.massKg + M_EFF_EXTRA_KG);
    const accel = clamp(rawAccel, -MAX_ACCEL_MPS2, MAX_ACCEL_MPS2);
    speed = clamp(speed + accel * step, 0, MAX_SPEED_MPS);

    const advance = advancePosition(world, edgeId, s, speed * step);
    edgeId = advance.edgeId;
    s = advance.s;
    distance += advance.moved;

    if (!advance.ok) {
      speed = 0;
      break;
    }
  }

  return {
    ...state,
    riderId,
    edgeId,
    s: Math.max(0, s),
    lane,
    speed: Number.isFinite(speed) ? speed : 0,
    power,
    distance: Number.isFinite(distance) ? distance : 0,
  };
}

/**
 * Maximum draft benefit from riders ahead on the same edge.
 * A rider 0.5 m behind saves about 35% of drag; 5 m behind about 6%.
 * Takes the maximum, not the sum, and ignores riders behind or on another edge.
 */
export function computeDraft(
  state: RiderState,
  neighbors: RiderState[],
): number {
  if (!Number.isFinite(state.edgeId) || !Number.isFinite(state.s)) return 0;

  const lane = finiteOr(state.lane, 0);
  let best = 0;

  for (const other of neighbors) {
    if (!other || other === state) continue;
    if (other.riderId === state.riderId) continue;
    if (other.edgeId !== state.edgeId) continue;
    if (!Number.isFinite(other.s) || !Number.isFinite(other.lane)) continue;

    const gap = other.s - state.s;
    if (!(gap > 0) || gap > DRAFT_MAX_GAP_M) continue;

    const lateral = Math.max(0, 1 - Math.abs(other.lane - lane) / DRAFT_LANE_WIDTH_M);
    if (lateral <= 0) continue;

    const benefit =
      DRAFT_BASE *
      Math.exp(-(Math.max(gap, DRAFT_MIN_GAP_M) - DRAFT_MIN_GAP_M) / DRAFT_DECAY_M) *
      lateral;
    if (benefit > best) best = benefit;
  }

  return Math.min(MAX_DRAFT, best);
}
